import React, { useEffect, useState } from 'react';
import { Card, CardContent } from "../../../../../../components/ui/card";
import { Button } from "../../../../../../components/ui/button";
import { Input } from "../../../../../../components/ui/input";
import { Label } from "../../../../../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../../components/ui/table";
import { Plus, Save, FileText, Download, Upload, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Tooltip } from "../../../../../../components/ui/tooltip";
import { Signature } from './Signature';
import { AntdDate } from '../../../../../../components/ui/antd-date';
import { MultiSelect } from '../../../../../../components/ui/multi-select';
interface ExaminationStage {
  id: number;
  stage: string;
  remarks: string;
  assessment: 'Accepted' | 'Rejected' | 'N/A';
}

interface ProjectDetails {
  client: string;
  projectNo: string;
  buckleArrestorAssyNo: string;
  welder: string;
  InspectionProcedure: string;
  acceptanceCriteria: string;
  wps: string;
  project: string;
  location: string;
  processes: string;
  date: string;
  pipeSize: string;
}

interface VisualInspectionProps {
  selectedItem: any;
  selectedPipe: any;
  materials: any;
  specifications: any;
  visualInspectionWeldData: any;
  visualInspectionState: any;
  onVisualInspectionStateChange: (state: any) => void;
  weldData: any;
  customerRepresentativeList: Array<{ value: string, label: string }>;
  qcList: Array<{ value: string, label: string }>;
  onDownloadReport?: () => void;
  downloadLoading?: boolean;
  isEditMode?: boolean;
  canEdit?: boolean;
  canReport?: boolean;
  canSign?: boolean;
  isCustomerRepresentativeUser?: boolean;
  isQCInspectorUser?: boolean;
  isWelderUser?: boolean;
  currentUserTeamMemberId?: string | number;
  isAdminOrSuperAdmin?: boolean;
}

export const VisualInspection: React.FC<VisualInspectionProps> = ({
  selectedItem,
  selectedPipe,
  materials,
  specifications,
  visualInspectionWeldData,
  visualInspectionState,
  onVisualInspectionStateChange,
  weldData,
  customerRepresentativeList,
  qcList,
  onDownloadReport,
  downloadLoading = false,
  isEditMode = false,
  canEdit = true,
  canReport = true,
  canSign = true,
  isCustomerRepresentativeUser = false,
  isQCInspectorUser = false,
  isWelderUser = false,
  currentUserTeamMemberId,
  isAdminOrSuperAdmin = false
}) => {

  const [startDate, setStartDate] = useState<string>(visualInspectionWeldData?.date || new Date().toISOString().split('T')[0]);
  const [selectedInspectionProcedureIds, setSelectedInspectionProcedureIds] = useState<string[]>(visualInspectionWeldData?.details?.inspectionProcedureIds || []);
  const [selectedAcceptanceCriteriaIds, setSelectedAcceptanceCriteriaIds] = useState<string[]>(visualInspectionWeldData?.details?.acceptanceCriteriaIds || []);
  const [selectedWeldProcessIds, setSelectedWeldProcessIds] = useState<string[]>(visualInspectionWeldData?.details?.weldProcessIds || []);

  // Helper function to check if examination results match current pass configuration
  const doExaminationResultsMatchPassConfig = (examinationResult: ExaminationStage[], weldPasses: any[]): boolean => {
    if (!examinationResult || examinationResult.length === 0) return false;
    if (!weldPasses || weldPasses.length === 0) return false;

    // Get pass names from examination results (excluding 'FIT UP')
    const examinationPassNames = examinationResult
      .filter(stage => stage.stage !== 'FIT UP')
      .map(stage => stage.stage)
      .sort();

    // Get pass names from current weld passes
    const currentPassNames = weldPasses
      .map((pass: any) => pass.passName || '')
      .filter((name: string) => name !== '')
      .sort();

    // Compare if they match
    if (examinationPassNames.length !== currentPassNames.length) return false;

    return examinationPassNames.every((name, index) => name === currentPassNames[index]);
  };

  // Helper function to ensure 'FIT UP' stage always stays first and has a unique id
  const ensureFitUpFirst = (stages: ExaminationStage[]): ExaminationStage[] => {

    const existingFitUpIndex = stages.findIndex(stage => stage.stage === 'FIT UP');
    const otherStages = stages.filter(stage => stage.stage !== 'FIT UP');
    const usedIds = new Set(otherStages.map(stage => stage.id));

    let fitUpStage: ExaminationStage;

    if (existingFitUpIndex >= 0) {
      fitUpStage = stages[existingFitUpIndex];
    } else {
      // If no FIT UP stage exists, create one
      fitUpStage = {
        id: 0,
        stage: 'FIT UP',
        remarks: 'N/A',
        assessment: 'N/A'
      };
    }

    // Ensure FIT UP has a unique id among other stages
    if (usedIds.has(fitUpStage.id)) {
      let newId = 0;
      while (usedIds.has(newId)) {
        newId += 1;
      }
      fitUpStage = { ...fitUpStage, id: newId };
    }

    return [fitUpStage, ...otherStages];
  };

  const [examinationStages, setExaminationStages] = useState<ExaminationStage[]>(() => {

    let stages: ExaminationStage[] = [];
    stages.push({
      id: 1,
      stage: 'FIT UP',
      remarks: 'N/A',
      assessment: 'N/A'
    });

    console.log('Visual Inspection Weld Data:', visualInspectionWeldData);
   // console.log ('Weld Data:', weldData);

    // Check if weldData and weldPasses exist
    if (!weldData || !weldData.weldPasses || weldData.weldPasses.length === 0) {
      return ensureFitUpFirst(stages);
    }

    // Initialize stages based on weld passes data
    // Only use examination results if they match the current pass configuration
    if (visualInspectionWeldData?.examinationResult && visualInspectionWeldData.examinationResult.length > 0) {
      // Check if examination results match current pass configuration
      const resultsMatch = doExaminationResultsMatchPassConfig(
        visualInspectionWeldData.examinationResult,
        weldData.weldPasses
      );

      if (resultsMatch) {
        // Examination results match current pass configuration, use them
        const weldPassStages = weldData.weldPasses.map((pass: any, index: number) => ({
          id: index + 2, // +2 because we already have FIT UP at index 1
          stage: pass.passName || `Weld-${index + 1}`,
          remarks: pass.passVisualInspectionRemarks || 'N/A',
          assessment: pass.passVisualInspectionAssessment || 'N/A'
        }));

        // Merge examination results with weld pass data
        const mergedStages = [...stages];

        // Add existing examination results
        mergedStages.push(...visualInspectionWeldData.examinationResult);

        // Append any weld passes that don't have examination results
        weldPassStages.forEach((weldStage: ExaminationStage) => {
          const existingStage = mergedStages.find(stage => stage.stage === weldStage.stage);
          if (!existingStage) {
            mergedStages.push(weldStage);
          }
        });

        return ensureFitUpFirst(mergedStages);
      } else {
        // Pass configuration has changed, skip examination results and start fresh
        stages.push(...weldData.weldPasses.map((pass: any, index: number) => ({
          id: index + 1 + 1,
          stage: pass.passName || `Weld-${index + 1}`,
          remarks: pass.passVisualInspectionRemarks || 'N/A',
          assessment: 'N/A'
        })));
        return ensureFitUpFirst(stages);
      }
    } else {
      // No examination results, initialize from weld passes
      stages.push(...weldData.weldPasses.map((pass: any, index: number) => ({
        id: index + 1 + 1,
        stage: pass.passName || `Weld-${index + 1}`,
        remarks: pass.passVisualInspectionRemarks || 'N/A',
        assessment: 'N/A'
      })));
      return ensureFitUpFirst(stages);
    }
  });

  const [notes, setNotes] = useState<string>(visualInspectionWeldData?.note || '');
  const [pipelineTechniqueName, setPipelineTechniqueName] = useState<string>('');
  const [pipelineTechniqueSignature, setPipelineTechniqueSignature] = useState<string>('');
  const [pipelineTechniqueDate, setPipelineTechniqueDate] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [companySignature, setCompanySignature] = useState<string>('');
  const [companyDate, setCompanyDate] = useState<string>('');

  const [customerSignature, setCustomerSignature] = useState<string | null>(visualInspectionWeldData?.customerRepresentativeId?.toString() || null);
  const [qcSignature, setQcSignature] = useState<string | null>(visualInspectionWeldData?.qcInspectorId?.toString() || null);



  // Create options for specifications dropdowns
  const inspectionProcedureOptions_visualInspection = specifications
    .filter((spec: any) => spec.type === 'INSPECTION_PROCEDURE')
    .map((inspectionProcedure: any) => {
      const files = inspectionProcedure.files?.[inspectionProcedure.files.length - 1]
      return {
        value: inspectionProcedure.id,
        standardName: files?.details?.inspectionProcedure?.standard,
        label: files?.fileName || `Inspection Procedure ${inspectionProcedure.id}`
      }

    });

  // Create options for specifications dropdowns
  const acceptanceCriteriaOptions_visualInspection = specifications
    .filter((spec: any) => spec.type === 'ACCEPTANCE_CRITERIA')
    .map((acceptanceCriteria: any) => {
      const files = acceptanceCriteria.files?.[acceptanceCriteria.files.length - 1]
      return {
        value: acceptanceCriteria.id,
        standardName: files?.details?.acceptanceCriteria?.standard,
        label: files?.fileName || `Acceptance Criteria ${acceptanceCriteria.id}`
      }

    });

  // Create options for specifications dropdowns
  const weldProcessOptions_visualInspection = specifications
    .filter((spec: any) => spec.type === 'WELD_PROCESS')
    .flatMap((weldProcess: any, index: number) => {
      if (index === 0) {

        // Try to get processType from different possible locations
        let processTypes = null;
        let fileName = null;

        // First try: files[0].details.weldProcess.processType (new structure)
        if (weldProcess.files?.[0]?.details?.weldProcess?.processType) {
          processTypes = weldProcess.files[0].details.weldProcess.processType;
          fileName = weldProcess.files[0].fileName;
        }
        // Second try: details.weldProcess.processType (mock data structure)
        else if (weldProcess.details?.weldProcess?.processType) {
          processTypes = weldProcess.details.weldProcess.processType;
          fileName = weldProcess.fileName;
        }


        // Handle both array and string cases
        if (Array.isArray(processTypes)) {
          return processTypes.map((process: any, index: number) => ({
            value: selectedWeldProcessIds.length > 0 && selectedWeldProcessIds[index] ? selectedWeldProcessIds[index] : index + 1,
            label: process
          }));
        } else if (typeof processTypes === 'string') {
          // If processType is a single string, convert it to an array with one item
          return [{
            value: selectedWeldProcessIds.length > 0 && selectedWeldProcessIds[0] ? selectedWeldProcessIds[0] : 1,
            label: processTypes
          }];
        }

        // If no processType found, try to use the fileName as a fallback
        if (fileName) {
          return [{
            value: selectedWeldProcessIds.length > 0 && selectedWeldProcessIds[0] ? selectedWeldProcessIds[0] : 1,
            label: fileName
          }];
        }
      }
      return [];
    });



  // // If no weld process options found, provide a default option
  // if (weldProcessOptions_visualInspection.length === 0) {
  //   weldProcessOptions_visualInspection.push({
  //     value: 1,
  //     label: 'Default Weld Process'
  //   });
  // }

  // Sync local state with parent state when visualInspectionWeldData or weldData changes
  useEffect(() => {
    if (visualInspectionWeldData) {



      // Initialize details object if it doesn't exist
      if (!visualInspectionWeldData.details) {
        visualInspectionWeldData.details = {
          inspectionProcedureIds: [],
          acceptanceCriteriaIds: [],
          weldProcessIds: [],
          note: ''
        };
      }

      // Restore existing data if available, otherwise use defaults
      setStartDate(visualInspectionWeldData.date || new Date().toISOString().split('T')[0]);
      if (!visualInspectionWeldData.date)
        visualInspectionWeldData.date = new Date().toISOString().split('T')[0];

      setSelectedInspectionProcedureIds(visualInspectionWeldData.details.inspectionProcedureIds || []);
      setSelectedAcceptanceCriteriaIds(visualInspectionWeldData.details.acceptanceCriteriaIds || []);
      setSelectedWeldProcessIds(visualInspectionWeldData.details.weldProcessIds || []);

     // console.log('Visual Inspection Weld Data:', (visualInspectionWeldData.details.weldProcessIds));
      setNotes(visualInspectionWeldData.details.note || '');
      setCustomerSignature(visualInspectionWeldData?.customerRepresentativeId?.toString() || null);
      setQcSignature(visualInspectionWeldData?.qcInspectorId?.toString() || null);

      // Update examination stages - only if they match current pass configuration
      if (weldData && weldData.weldPasses && weldData.weldPasses.length > 0) {
        if (visualInspectionWeldData.examinationResult && visualInspectionWeldData.examinationResult.length > 0) {
          // Check if examination results match current pass configuration
          const resultsMatch = doExaminationResultsMatchPassConfig(
            visualInspectionWeldData.examinationResult,
            weldData.weldPasses
          );

          if (resultsMatch) {
            // Examination results match, use them
            setExaminationStages(visualInspectionWeldData.examinationResult);
          } else {
            // Pass configuration has changed, initialize fresh stages from current weld passes
            const newStages: ExaminationStage[] = [{
              id: 1,
              stage: 'FIT UP',
              remarks: 'N/A',
              assessment: 'N/A'
            }];
            newStages.push(...weldData.weldPasses.map((pass: any, index: number) => ({
              id: index + 2,
              stage: pass.passName || `Weld-${index + 1}`,
              remarks: pass.passVisualInspectionRemarks || 'N/A',
              assessment: 'N/A'
            })));
            setExaminationStages(ensureFitUpFirst(newStages));
          }
        } else {
          // No examination results, initialize from current weld passes
          const newStages: ExaminationStage[] = [{
            id: 1,
            stage: 'FIT UP',
            remarks: 'N/A',
            assessment: 'N/A'
          }];
          newStages.push(...weldData.weldPasses.map((pass: any, index: number) => ({
            id: index + 2,
            stage: pass.passName || `Weld-${index + 1}`,
            remarks: pass.passVisualInspectionRemarks || 'N/A',
            assessment: 'N/A'
          })));
          setExaminationStages(ensureFitUpFirst(newStages));
        }
      } else if (visualInspectionWeldData.examinationResult && visualInspectionWeldData.examinationResult.length > 0) {
        // No weld passes but have examination results, use them
        setExaminationStages(visualInspectionWeldData.examinationResult);
      }
    } else {
      // Set default values when no existing data
      setStartDate(new Date().toISOString().split('T')[0]);

      setSelectedInspectionProcedureIds([]);
      setSelectedAcceptanceCriteriaIds([]);
      setSelectedWeldProcessIds([]);
      setNotes('');
      setCustomerSignature(null);
      setQcSignature(null);
      
      // Initialize stages from current weld passes if available
      if (weldData && weldData.weldPasses && weldData.weldPasses.length > 0) {
        const newStages: ExaminationStage[] = [{
          id: 1,
          stage: 'FIT UP',
          remarks: 'N/A',
          assessment: 'N/A'
        }];
        newStages.push(...weldData.weldPasses.map((pass: any, index: number) => ({
          id: index + 2,
          stage: pass.passName || `Weld-${index + 1}`,
          remarks: pass.passVisualInspectionRemarks || 'N/A',
          assessment: 'N/A'
        })));
        setExaminationStages(ensureFitUpFirst(newStages));
      } else {
        // No weld data, keep only FIT UP stage
        const defaultStages: ExaminationStage[] = [{
          id: 1,
          stage: 'FIT UP',
          remarks: 'N/A',
          assessment: 'N/A'
        }];
        setExaminationStages(ensureFitUpFirst(defaultStages));
      }
    }
  }, [visualInspectionWeldData, weldData]);

  const handleStageChange = (id: number, field: keyof ExaminationStage, value: string | 'Accepted' | 'Rejected' | 'N/A') => {
    setExaminationStages(prev => {
      const updatedStages = prev.map(stage => {
        if (stage.id === id) {
          // If clicking the same assessment value, toggle it off (set to N/A)
          if (field === 'assessment' && stage.assessment === value) {
            return { ...stage, [field]: 'N/A' as const };
          }
          // Otherwise, set the new value
          return { ...stage, [field]: value as any };
        }
        return stage;
      });

      // // Convert stages to weld passes format and update parent state
      // const weldPasses = updatedStages.map(stage => ({
      //   weldPassId: stage.id,
      //   passVisualInspectionRemarks: stage.assessment === 'Accepted'
      // }));

      if (visualInspectionWeldData) {
        visualInspectionWeldData.examinationResult = updatedStages;
        if (onVisualInspectionStateChange) {
          onVisualInspectionStateChange(true);
        }
      }

      return ensureFitUpFirst(updatedStages);
    });
  };

  const addNewStage = () => {
    setExaminationStages(prev => {
      const newStage: ExaminationStage = {
        id: prev.length + 1,
        stage: '',
        remarks: 'N/A',
        assessment: 'N/A'
      };
      const updatedStages = [...prev, newStage];

      // Convert stages to weld passes format and update parent state
      const weldPasses = updatedStages.map(stage => ({
        weldPassId: stage.id,
        passVisualInspectionRemarks: stage.remarks,
        passVisualInspectionAssessment: stage.assessment === 'Accepted'
      }));

      if (visualInspectionWeldData) {
        visualInspectionWeldData.examinationResult = weldPasses;
        if (onVisualInspectionStateChange) {
          onVisualInspectionStateChange(true);
        }
      }

      return ensureFitUpFirst(updatedStages);
    });
  };

  const handleNotesChange = (newNotes: string) => {
    setNotes(newNotes);
    visualInspectionWeldData.details.note = newNotes;
    if (onVisualInspectionStateChange) {
      onVisualInspectionStateChange(true);
    }
  };

  const handleCustomerSignatureChange = (value: string | null) => {

    setCustomerSignature(value);
    visualInspectionWeldData.customerRepresentativeId = value;
    if (onVisualInspectionStateChange) {
      onVisualInspectionStateChange(true);
    }
  };

  const handleQcSignatureChange = (signature: string | null) => {
    setQcSignature(signature);
    visualInspectionWeldData.qcInspectorId = signature;
    if (onVisualInspectionStateChange) {
      onVisualInspectionStateChange(true);
    }
  };

  const OnHandleSignature = (comment: { signature: string; name: string; comment?: string; stampImage?: string; type: 'customer' | 'qc' }) => {
    if (visualInspectionWeldData) {
      visualInspectionWeldData.signature = {
        ...visualInspectionWeldData.signature,
        [comment.type]: {
          signature: comment.signature,
          name: comment.name,
          comment: comment.comment,
          stampImage: comment.stampImage,
          timestamp: new Date().toISOString()
        }
      };
      if (onVisualInspectionStateChange) {
        onVisualInspectionStateChange(true);
      }
    }
    console.log(visualInspectionWeldData);
  };

  const handleInspectionProcedureChange_visualInspection = (value: string[]) => {
    setSelectedInspectionProcedureIds(value);
    visualInspectionWeldData.details.inspectionProcedureIds = value;
    if (onVisualInspectionStateChange) {
      onVisualInspectionStateChange(true);
    }
  };

  const handleAcceptanceCriteriaChange_visualInspection = (value: string[]) => {
    setSelectedAcceptanceCriteriaIds(value);
    visualInspectionWeldData.details.acceptanceCriteriaIds = value;
    if (onVisualInspectionStateChange) {
      onVisualInspectionStateChange(true);
    }
  };

  const handleWeldProcessChange_visualInspection = (value: string[]) => {
    setSelectedWeldProcessIds(value);
    visualInspectionWeldData.details.weldProcessIds = value;
    if (onVisualInspectionStateChange) {
      onVisualInspectionStateChange(true);
    }
  };

  return (
    <div className="space-y-6">

      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <h4 className="text-lg font-semibold text-gray-700">Visual Inspection Details</h4>
            </div>
            
            {/* Main Form Fields */}
            <div className="grid grid-cols-1 lg:grid-cols-7 gap-2">


              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Date</Label>
                <div className="flex items-center space-x-2">
                  <AntdDate
                    name="startDate"
                    value={startDate}
                    onChange={(value: string) => {
                      setStartDate(value);
                      if (visualInspectionWeldData) {
                        visualInspectionWeldData.date = value;
                        if (onVisualInspectionStateChange) {
                          onVisualInspectionStateChange(true);
                        }
                      }
                    }}
                    disabled={!isEditMode}
                  />

                </div>
              </div>


              <div className="space-y-2 col-span-2">
                <Label className="text-sm font-medium text-gray-700">Inspection Procedure</Label>
                <div className="w-full">
                  <MultiSelect
                    options={inspectionProcedureOptions_visualInspection || []}
                    selected={selectedInspectionProcedureIds}
                    onChange={isEditMode ? handleInspectionProcedureChange_visualInspection : () => {}}
                  />
                </div>
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-sm font-medium text-gray-700">Acceptance Criteria</Label>
                <div className="w-full">
                  <MultiSelect
                    options={acceptanceCriteriaOptions_visualInspection || []}
                    selected={selectedAcceptanceCriteriaIds}
                    onChange={isEditMode ? handleAcceptanceCriteriaChange_visualInspection : () => {}}
                  />
                </div>
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-sm font-medium text-gray-700">Weld Process</Label>
                <div className="w-full">
                  <MultiSelect
                    options={weldProcessOptions_visualInspection || []}
                    selected={selectedWeldProcessIds}
                    onChange={isEditMode ? handleWeldProcessChange_visualInspection : () => {}}
                  />
                </div>
              </div>
            </div>



          </div>
        </CardContent>
      </Card>

      {/* Examination Result Table */}
      <Card>
        <CardContent className="p-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-700 mb-2">Examination Result</h4>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center w-48">STAGE</TableHead>
                  <TableHead className="text-center w-48">REMARKS</TableHead>
                  <TableHead className="text-center w-48">ASSESSMENT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {examinationStages.map((stage) => (
                  <TableRow key={stage.id}>
                    <TableCell className="text-center">
                      <span className="text-sm font-medium">{stage.stage}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        value={stage.remarks}
                        onChange={(e) => handleStageChange(stage.id, 'remarks', e.target.value)}
                        className="h-8 text-sm text-center border border-gray-300 focus:ring-0 focus:border-gray-400 bg-white"
                        placeholder="Enter remarks"
                        disabled={!isEditMode}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center items-center space-x-3">
                        <Tooltip content="Accepted" placement="top">
                          <button
                            onClick={() => handleStageChange(stage.id, 'assessment', 'Accepted')}
                            className={`p-2 rounded ${stage.assessment === 'Accepted'
                              ? 'bg-green-100 text-green-600'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                            aria-label="Accept"
                            disabled={!isEditMode}
                          >
                            <ThumbsUp className="h-6 w-6" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Rejected" placement="top">
                          <button
                            onClick={() => handleStageChange(stage.id, 'assessment', 'Rejected')}
                            className={`p-2 rounded ${stage.assessment === 'Rejected'
                              ? 'bg-red-100 text-red-600'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                            aria-label="Reject"
                            disabled={!isEditMode}
                          >
                            <ThumbsDown className="h-6 w-6" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Not Available" placement="top">
                          <button
                            onClick={() => handleStageChange(stage.id, 'assessment', 'N/A')}
                            className={`p-2 rounded ${stage.assessment === 'N/A'
                              ? 'text-[#B38600] bg-[rgba(230,161,0,0.08)]'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                            aria-label="Not Applicable"
                            disabled={!isEditMode}
                          >
                            <span className="text-sm font-medium">N/A</span>
                          </button>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Total Stages: {examinationStages.length}
            </div>
            {/* <Button onClick={addNewStage} variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Stage
            </Button> */}
          </div>
        </CardContent>
      </Card>

      {/* Notes Section */}
      <Card>
        <CardContent className="p-6">
          <Label className="text-base font-medium">Notes:</Label>
          <textarea
            value={notes}
            onChange={(e) => handleNotesChange(e.target.value)}
            className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[100px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:border-blue-700"
            placeholder="Enter any additional notes or observations..."
            disabled={!isEditMode}
          />
        </CardContent>
      </Card>

      {/* Signature Section */}
      <Signature
        customerSignature={customerSignature}
        qcSignature={qcSignature}
        customerRepresentativeList={customerRepresentativeList}
        qcList={qcList}
        handleQcSignatureChange={handleQcSignatureChange}
        handleCustomerSignatureChange={handleCustomerSignatureChange}
        onSignatureChange={OnHandleSignature}
        signatureData={visualInspectionWeldData}
        canSign={canSign}
        canEdit={canEdit}
        isEditMode={isEditMode}
        isCustomerRepresentativeUser={isCustomerRepresentativeUser}
        isQCInspectorUser={isQCInspectorUser}
        isWelderUser={isWelderUser}
        currentUserTeamMemberId={currentUserTeamMemberId}
        isAdminOrSuperAdmin={isAdminOrSuperAdmin}
      />

      {/* Action Buttons */}
      {/* <div className="flex justify-end space-x-3">
        <Button variant="outline" onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Import
        </Button>
        <Button onClick={handleSave}>
          <Save className="h-4 w-4 mr-2" />
          Save Visual Inspection
        </Button>
      </div> */}
    </div>
  );
}; 